import { z } from "zod"
import { Errors } from "../errors"
import { EmployeeLocale } from "../shared/common"

const freeEmailDomains = [
  "gmail.com",
  "gmail.co",
  "yahoo.com",
  "outlook.com",
  "hotmail.com",
  "aol.com",
  "icloud.com",
  "protonmail.com",
  "zoho.com",
  "mail.com",
  "gmx.com",
  "gmx.de",
  "yandex.com",
  "yandex.ru",
  "fastmail.com",
  "live.com",
  "msn.com",
]

const LoginEmailSchema = z.string().trim().toLowerCase().email(Errors.EMAIL_INVALID)

export const isFreeEmailDomain = (email: string) => {
  const domain = email.split("@")[1]?.toLowerCase()
  if (!domain) return true
  return freeEmailDomains.includes(domain)
}

export const LoginSchema = z.strictObject({
  email: LoginEmailSchema,
  password: z
    .string({ error: Errors.PASSWORD_REQUIRED })
    .min(5, { message: Errors.PASSWORD_INVALID }),
})

export const SignupSchema = z.strictObject({
  name: z.string({ error: Errors.NAME_REQUIRED }).min(1, { message: Errors.NAME_REQUIRED }),
  email: z
    .email(Errors.EMAIL_INVALID)
    .refine((email) => !isFreeEmailDomain(email), { message: Errors.EMAIL_NOT_CORPORATE }),
  role: z.string({ error: Errors.ROLE_IS_REQUIRED }),
  companySize: z.string({ error: Errors.COMPANY_SIZE_REQUIRED }),
  locale: z.enum(EmployeeLocale).default("en_US"),
})

export const ForgotPasswordSchema = z.strictObject({
  email: z.email(Errors.EMAIL_INVALID),
})

export const ResetPasswordSchema = z
  .strictObject({
    token: z.string(),
    password: z
      .string({ error: Errors.PASSWORD_REQUIRED })
      .min(5, { message: Errors.PASSWORD_MIN_LENGTH }),
    confirmPassword: z.string({ error: Errors.PASSWORD_REQUIRED }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ["confirmPassword"],
    message: Errors.PASSWORD_DONT_MATCH,
  })

export const GetInviteSchema = z.strictObject({
  token: z.string({ error: Errors.INVITATION_TOKEN_NOT_FOUND }).min(1, {
    message: Errors.INVITATION_TOKEN_NOT_FOUND,
  }),
})

export const SignupInvitationSchema = z.strictObject({
  password: z
    .string({ error: Errors.PASSWORD_REQUIRED })
    .min(5, { message: Errors.PASSWORD_INVALID }),
  name: z.string({ error: Errors.NAME_REQUIRED }).min(1, { message: Errors.NAME_REQUIRED }),
  token: z.string({ error: Errors.INVITATION_TOKEN_NOT_FOUND }).min(1, {
    message: Errors.INVITATION_TOKEN_NOT_FOUND,
  }),
})

export const SwitchCompanySchema = z.object({
  companyId: z.string({ error: Errors.COMPANY_ID_REQUIRED }),
})

export const SwitchOrganizationSchema = z.object({
  organizationId: z.string({ error: Errors.ORGANIZATION_ID_REQUIRED }),
})

export const SignupByWebsiteSchema = z.strictObject({
  email: z.email(Errors.EMAIL_INVALID),
  phone: z
    .string({ error: Errors.FIELD_REQUIRED })
    .trim()
    .min(1, { message: Errors.FIELD_REQUIRED }),
  locale: z.enum(EmployeeLocale).catch("en_US"),
})

export const CompleteSignupByWebsiteSchema = z
  .strictObject({
    setupToken: z.string(),
    companyName: z.string().min(1, { message: Errors.COMPANY_NAME_IS_REQUIRED }),
    name: z.string().min(1, { message: Errors.NAME_REQUIRED }),
    password: z
      .string({ error: Errors.PASSWORD_REQUIRED })
      .min(5, { message: Errors.PASSWORD_MIN_LENGTH }),
    confirmPassword: z.string({ error: Errors.PASSWORD_REQUIRED }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ["confirmPassword"],
    message: Errors.PASSWORD_DONT_MATCH,
  })
